import React from 'react';
import { motion } from 'framer-motion';
import { XCircleIcon, UserIcon, BookOpenIcon, TargetIcon, BanIcon } from 'lucide-react';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 12
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-40px'
  },
  transition: {
    duration: 0.3
  }
};
export function PainSection() {
  return <section className="w-full bg-[#1a0b3b] py-16 md:py-24 px-5" data-id="element-292">
      <div className="max-w-3xl mx-auto" data-id="element-293">
        {/* Para quién es */}
        <motion.div {...fadeIn} data-id="element-294">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-8" data-id="element-295">
            ¿Para quién es esto?
          </h2>

          <div className="space-y-4 mb-10" data-id="element-296">
            <ParaQuienItem text="Emprendedores que quieren ser más productivos sin sacrificar su bienestar." data-id="element-297" />
            <ParaQuienItem text="Profesionales que buscan gestionar mejor sus finanzas y su tiempo." data-id="element-298" />
            <ParaQuienItem text="Coaches y líderes que necesitan herramientas prácticas para crecer." data-id="element-299" />
            <ParaQuienItem text="Estudiantes comprometidos con su desarrollo personal y espiritual." data-id="element-300" />
          </div>
        </motion.div>

        <motion.div {...fadeIn} transition={{
        duration: 0.3,
        delay: 0.05
      }} data-id="element-301">
          <div className="border border-white/5 rounded-xl bg-white/[0.02] p-6 md:p-8 mb-14" data-id="element-302">
            <p className="text-amber-500 font-semibold text-sm uppercase tracking-wide mb-4" data-id="element-303">
              No es para ti si buscas:
            </p>
            <div className="space-y-3 text-gray-400" data-id="element-304">
              <NoParaItem text="Quien busca resultados sin ningún esfuerzo" data-id="element-305" />
              <NoParaItem text="Quien no está dispuesto a invertir ni 5 minutos en sí mismo" data-id="element-306" />
              <NoParaItem text="Quien espera una fórmula mágica instantánea" data-id="element-307" />
            </div>
          </div>
        </motion.div>

        {/* El patrón real */}
        <motion.div {...fadeIn} transition={{
        duration: 0.3,
        delay: 0.08
      }} data-id="element-308">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-309">
            El patrón real
          </h2>
          <p className="text-lg text-gray-300 mb-3" data-id="element-310">
            Tú no tienes "un problema de motivación".
          </p>
          <p className="text-lg text-white font-semibold mb-8" data-id="element-311">
            Tienes un problema de arquitectura diaria.
          </p>
          <p className="text-gray-400 mb-8" data-id="element-312">
            Porque cada vez que intentas cambiar, haces lo mismo:
          </p>
        </motion.div>

        {/* The cycle */}
        <motion.div {...fadeIn} transition={{
        duration: 0.3,
        delay: 0.1
      }} className="space-y-0 mb-10" data-id="element-313">
          <CycleStep number="1" text="Te emocionas" data-id="element-314" />
          <CycleStep number="2" text="Te exiges demasiado" data-id="element-315" />
          <CycleStep number="3" text="Fallas" data-id="element-316" />
          <CycleStep number="4" text="Te culpas" data-id="element-317" />
          <CycleStep number="5" text="Abandonas" data-id="element-318" />
          <CycleStep number="6" text="Vuelves a empezar semanas después" isLast data-id="element-319" />
        </motion.div>

        <motion.div {...fadeIn} transition={{
        duration: 0.3,
        delay: 0.12
      }} data-id="element-320">
          <p className="text-gray-400 text-lg mb-2" data-id="element-321">Y así se te va el año.</p>
          <p className="text-white text-xl font-semibold" data-id="element-322">
            Lo que estás perdiendo no es un hábito.{' '}
            <span className="text-amber-500" data-id="element-323">Es confianza personal.</span>
          </p>
        </motion.div>

        {/* Why old solutions fail */}
        <motion.div {...fadeIn} transition={{
        duration: 0.3,
        delay: 0.14
      }} className="mt-16" data-id="element-324">
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-8" data-id="element-325">
            Por qué lo que has probado no funciona
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-326">
            <FailCard icon={<BookOpenIcon className="w-5 h-5" data-id="element-328" />} title="Inspiración sin estructura" text="Te emocionas hoy, mañana vuelves a lo mismo." data-id="element-327" />
            <FailCard icon={<TargetIcon className="w-5 h-5" data-id="element-330" />} title="Fuerza de voluntad como estrategia" text="Cuando llega cansancio, estrés o tentación, pierdes." data-id="element-329" />
            <FailCard icon={<UserIcon className="w-5 h-5" data-id="element-332" />} title="Metas gigantes" text="Te exiges como si tu vida no tuviera caos." data-id="element-331" />
            <FailCard icon={<BanIcon className="w-5 h-5" data-id="element-334" />} title="Todo-o-nada" text='Fallas un día y tu mente lo convierte en "ya perdí".' data-id="element-333" />
          </div>
        </motion.div>

        {/* The uncomfortable truth */}
        <motion.div {...fadeIn} transition={{
        duration: 0.3,
        delay: 0.16
      }} className="mt-16 border-l-2 border-amber-500/40 pl-6" data-id="element-335">
          <p className="text-lg text-gray-300 mb-3" data-id="element-336">
            Dios puede darte convicción…
          </p>
          <p className="text-xl text-white font-semibold mb-3" data-id="element-337">
            pero tú necesitas un sistema.
          </p>
          <p className="text-gray-400" data-id="element-338">
            Porque la fe no se prueba en una emoción del domingo. Se prueba el
            martes, con sueño, con estrés, con tentación, con mil cosas.
          </p>
          <p className="text-gray-400 mt-2" data-id="element-339">
            Y ahí es donde la mayoría se cae.
          </p>
        </motion.div>
      </div>

      <div className="section-divider mt-16 md:mt-24" aria-hidden="true" data-id="element-340" />
    </section>;
}
function ParaQuienItem({
  text
}: {
  text: string;
}) {
  return <div className="flex items-start gap-3" data-id="element-341">
      <span className="text-amber-500 mt-1 flex-shrink-0" data-id="element-342">✓</span>
      <p className="text-gray-200 text-lg" data-id="element-343">{text}</p>
    </div>;
}
function NoParaItem({
  text
}: {
  text: string;
}) {
  return <div className="flex items-start gap-3" data-id="element-344">
      <XCircleIcon className="w-4 h-4 text-gray-600 mt-1 flex-shrink-0" data-id="element-345" />
      <p data-id="element-346">{text}</p>
    </div>;
}
function CycleStep({
  number,
  text,
  isLast
}: {
  number: string;
  text: string;
  isLast?: boolean;
}) {
  return <div className="flex items-stretch gap-4" data-id="element-347">
      <div className="flex flex-col items-center" data-id="element-348">
        <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-sm text-gray-400 font-medium flex-shrink-0" data-id="element-349">
          {number}
        </div>
        {!isLast && <div className="w-px flex-1 bg-white/5 my-1" data-id="element-350" />}
      </div>
      <p className="text-gray-300 text-lg pb-4" data-id="element-351">{text}</p>
    </div>;
}
function FailCard({
  icon,
  title,
  text
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}) {
  return <div className="bg-white/[0.02] border border-white/5 rounded-xl p-5" data-id="element-352">
      <div className="flex items-center gap-2 mb-2" data-id="element-353">
        <span className="text-amber-500/70" data-id="element-354">{icon}</span>
        <h4 className="text-white font-semibold text-sm" data-id="element-355">{title}</h4>
      </div>
      <p className="text-gray-400 text-sm leading-relaxed" data-id="element-356">{text}</p>
    </div>;
}